import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store';
import { fetchUser, setExpiration, initScannerData } from './userActions';

export enum eRoleType {
  UNKNOWN = 0,
  OPERATOR = 1,
  GLOBAL_OPERATOR = 2,
  OPERATOR_MANAGE_SITES = 3,
  DASHBOARD = 4,
  CUSTOM_USER = 5,
  ViewOnly = 6
}

export interface TimeZoneSettings {
  TimeZone: string;
}
export interface DateTimeSettings {
  TimeZoneSettings: TimeZoneSettings;
}

export interface User {
  userId: number | undefined;
  userName: string;
  permissions: number[];
  email: string;
  level: string;
  isPowerUser: boolean;
  userRoleId: number | undefined;
  helpBaseUrl: string;
  userHash: string;
  clientId: string;
  tagName: string;
  enabledFeatures: number[];
  isGlobalMode: boolean;
  userRoleType: eRoleType;
  dbTenantId: string;
  dateTimeSettings: DateTimeSettings;
}
export interface UserState {
  loggedUser: User;
  isWelcomeModalOpen: boolean;
  expirationDate: string;
  isUpgradeModalOpen: boolean;
  disableInstallButton: boolean;
  loading: boolean;
}

const initialState: UserState = {
  loggedUser: {
    userId: undefined,
    userName: '',
    permissions: [],
    email: '',
    level: '',
    isPowerUser: false,
    userRoleId: undefined,
    helpBaseUrl: '',
    userHash: '',
    clientId: '',
    tagName: '',
    enabledFeatures: [],
    userRoleType: eRoleType.UNKNOWN,
    isGlobalMode: false,
    dbTenantId: '',
    dateTimeSettings: {
      TimeZoneSettings: {
        TimeZone: 'Local'
      }
    }
  },
  loading: false,
  isWelcomeModalOpen: false,
  expirationDate: '',
  isUpgradeModalOpen: false,
  disableInstallButton: true
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIsWelcomeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isWelcomeModalOpen = action.payload;
    },
    setIsUpgradeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isUpgradeModalOpen = action.payload;
    },
    setDisableInstallButton: (state, action: PayloadAction<boolean>) => {
      state.disableInstallButton = action.payload;
    },
    setDateTimeSettings: (state, action: PayloadAction<DateTimeSettings>) => {
      state.loggedUser.dateTimeSettings = action.payload;
    },
    resetExpiration: state => {
      state.expirationDate = '';
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchUser.fulfilled, (state, { payload }) => {
      // powerUser is now globalOperator
      payload.isPowerUser = payload.userRoleType === eRoleType.GLOBAL_OPERATOR;
      state.loggedUser = payload;
    });
    builder.addCase(setExpiration.fulfilled, (state, { payload }) => {
      state.disableInstallButton = false;
      state.expirationDate = payload ? JSON.parse(payload) : '';
    });
    builder.addCase(setExpiration.rejected, state => {
      state.expirationDate = '';
      state.disableInstallButton = false;
    });
    builder.addCase(initScannerData.fulfilled, (state, { payload }) => {
      state.isWelcomeModalOpen = payload.length === 0;
    });
  }
});

export const isLoggedIn = createSelector(
  (state: RootState) => state?.user.loggedUser.userName,
  (userName: string) => userName !== '' && userName !== undefined
);

export const isUserLoaded = (state: RootState): boolean => (state?.user.loggedUser.userName ?? '') !== '';

export const {
  setLoading,
  setIsWelcomeModalOpen,
  setIsUpgradeModalOpen,
  setDisableInstallButton,
  resetExpiration,
  setDateTimeSettings
} = userSlice.actions;
export default userSlice.reducer;
